import React from 'react';

export const GRANT_ACTING_PRIMARY_CONFIRMATION_TEXT = (
    <>
        <p>
            As the primary representative, you may grant no more than one team
            member the role of active primary representative. This will allow
            this member to manage team members (add, remove, and edit access),
            assign members to engagements, and update the company's payment
            method.
        </p>
        <p>
            By confirming this acting primary representative, you will{' '}
            <b>relinquish your own primary member permissions.</b> You can
            restore your own primary representative permissions at any time by
            removing the acting primary representative role from a team member.
        </p>
        Would you like to confirm the authorization of this acting
        representative?
    </>
);

export const ASSOCIATE_MEMBER_ROLES = [
    { id: 1, name: 'Fellow Practice Owner' },
    { id: 2, name: 'Practice Manager' },
    { id: 3, name: 'Company Bookkeeper' },
    { id: 4, name: 'Outside Accountant' },
    { id: 5, name: 'Outside Advisor' },
];

export const ASSOCIATE_MEMBER_ROLES_ENUM = {
    FellowPracticeOwner: 1,
    PracticeManager: 2,
    CompanyBookkeeper: 3,
    OutsideAccountant: 4,
    OutsideAdvisor: 5,
};

export const ASSOCIATE_MEMBER_STATUS = [
    { id: 0, name: 'Pending' },
    { id: 1, name: 'Approved' },
];

export const ASSOCIATE_MEMBER_RIGHTS = [
    { id: 1, name: 'IPOC' },
    { id: 2, name: 'Knowledge Library' },
    { id: 3, name: 'Valuation Reports Input Form' },
    { id: 4, name: 'Valuation Reports Procurement' },
    { id: 5, name: 'Valuation Reports Viewing' },
    { id: 6, name: 'Referral Partner Extension' },
    { id: 7, name: 'PmgRepresentativeAccessRights' },
    { id: 8, name: 'KPI Benchmarking Input Forms' },
    { id: 9, name: 'KPI Benchmarking Procurement' },
    { id: 10, name: 'KPI Benchmarking Viewing' },
    { id: 11, name: 'Accounting Standardization Input Forms' },
    { id: 12, name: 'Accounting Standardization Procurement' },
    { id: 13, name: 'Practice Sale InputForms' },
    { id: 14, name: 'Practice Sale Procurement' },
    { id: 15, name: 'Practice Sale Viewing' },
];

export const SELLER_TM_ROLE = {
    FellowPracticeOwner: 1,
    OutsideAdvisor: 5,
};

export const MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION = {
    FullReadOnlyMarketplace: 1,
    PracticeDataAndDocs: 2,
    CommsHub: 3,
    DataRoom: 4,
};

export const MARKETPLACE_SELLER_TM_PERMISSION_OPTIONS: {
    id: number;
    label: string;
    tooltip?: string;
}[] = [
    {
        id: MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.FullReadOnlyMarketplace,
        label: 'Full Read-Only Marketplace Access',
        tooltip:
            'Read-only access to this engagement: view the engagement tabs, documents, deal timeline, buyers, and meeting proposals. Cannot edit, upload, post messages, or schedule meetings.',
    },
    {
        id: MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.PracticeDataAndDocs,
        label: 'Practice Data & Docs View & Upload (via Connect)',
        tooltip:
            'View and upload the practice\'s data and documents in this engagement\'s Data & Docs tab — complete questionnaires and respond to buyer data requests.',
    },
    {
        id: MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.CommsHub,
        label: 'Read & Write Comms Hub Access',
        tooltip:
            'Read and post in this engagement\'s Communications Hub — message the broker and buyers, and propose, update, approve, or schedule meetings. Without this permission, the Comms Hub is read-only.',
    },
    {
        id: MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.DataRoom,
        label: 'Data Room',
    },
];

export const MARKETPLACE_SELLER_TM_PERMISSIONS_BY_ROLE: Record<
    number,
    number[]
> = {
    [SELLER_TM_ROLE.FellowPracticeOwner]: [
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.FullReadOnlyMarketplace,
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.PracticeDataAndDocs,
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.CommsHub,
    ],
    [SELLER_TM_ROLE.OutsideAdvisor]: [
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.FullReadOnlyMarketplace,
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.PracticeDataAndDocs,
        MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.CommsHub,
    ],
};

export function getAllowedMarketplacePermissionsForSellerTMRole(
    roleId: number
): number[] {
    return MARKETPLACE_SELLER_TM_PERMISSIONS_BY_ROLE[roleId] ?? [];
}

export const ASSOCIATE_MEMBER_RIGHTS_ENUM = {
    POC: 1,
    KnowledgeBase: 2,
    ValuationReportInputForms: 3,
    ValuationReportsProcurement: 4,
    ValuationReportsViewing: 5,
    ReferralPartnerExtension: 6,
    PmgRepresentativeAccessRights: 7,
    KpiBenchmarkingInputForms: 8,
    KpiBenchmarkingProcurement: 9,
    KpiBenchmarkingViewing: 10,
    AccountingStandardizationInputForms: 11,
    AccountingStandardizationProcurement: 12,
    PracticeSalesInputForms: 13,
    PracticeSalesProcurement: 14,
    PracticeSalesViewing: 15,
};

export interface AssociateMemberRights {
    practiceOwnerCommunity: boolean;
    knowledgeBase: boolean;
    valuationReportInputForms: boolean;
    valuationReportsProcurement: boolean;
    valuationReportsViewing: boolean;
    referralPartnerExtension: boolean;
    pmgRepresentativeAccessRights: boolean;
    kpiBenchmarkingInputForms: boolean;
    kpiBenchmarkingProcurement: boolean;
    kpiBenchmarkingViewing: boolean;
    accountingStandardizationInputForms: boolean;
    accountingStandardizationProcurement: boolean;
    practiceSalesInputForms: boolean;
    practiceSalesProcurement: boolean;
    practiceSalesViewing: boolean;
}

export const getDefaultAssosiateMemberRights = (): AssociateMemberRights => {
    return {
        practiceOwnerCommunity: false,
        knowledgeBase: false,
        valuationReportInputForms: false,
        valuationReportsProcurement: false,
        valuationReportsViewing: false,
        referralPartnerExtension: false,
        pmgRepresentativeAccessRights: false,
        kpiBenchmarkingInputForms: false,
        kpiBenchmarkingProcurement: false,
        kpiBenchmarkingViewing: false,
        accountingStandardizationInputForms: false,
        accountingStandardizationProcurement: false,
        practiceSalesInputForms: false,
        practiceSalesProcurement: false,
        practiceSalesViewing: false,
    };
};

export const getMappedAssosiateMemberRights = (
    numericArrayToMap: number[]
): AssociateMemberRights => {
    if (!numericArrayToMap) {
        return getDefaultAssosiateMemberRights();
    }

    const result: AssociateMemberRights = {
        practiceOwnerCommunity: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.POC
        ),
        knowledgeBase: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.KnowledgeBase
        ),
        valuationReportInputForms: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportInputForms
        ),
        valuationReportsProcurement: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportsProcurement
        ),
        valuationReportsViewing: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportsViewing
        ),
        referralPartnerExtension: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.ReferralPartnerExtension
        ),
        pmgRepresentativeAccessRights: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.PmgRepresentativeAccessRights
        ),
        kpiBenchmarkingInputForms: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingInputForms
        ),
        kpiBenchmarkingProcurement: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingProcurement
        ),
        kpiBenchmarkingViewing: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingViewing
        ),
        accountingStandardizationInputForms: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.AccountingStandardizationInputForms
        ),
        accountingStandardizationProcurement: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.AccountingStandardizationProcurement
        ),
        practiceSalesInputForms: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesInputForms
        ),
        practiceSalesProcurement: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesProcurement
        ),
        practiceSalesViewing: numericArrayToMap.includes(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesViewing
        ),
    };
    return result;
};

export const acceptedAccessRightsToNumbers = (
    accessRights: AssociateMemberRights
) => {
    const result = [];

    if (accessRights.practiceOwnerCommunity) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.POC);
    }

    if (accessRights.knowledgeBase) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.KnowledgeBase);
    }

    if (accessRights.valuationReportInputForms) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportInputForms);
    }

    if (accessRights.valuationReportsProcurement) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportsProcurement);
    }

    if (accessRights.valuationReportsViewing) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.ValuationReportsViewing);
    }

    if (accessRights.referralPartnerExtension) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.ReferralPartnerExtension);
    }

    if (accessRights.pmgRepresentativeAccessRights) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.PmgRepresentativeAccessRights);
    }

    if (accessRights.kpiBenchmarkingInputForms) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingInputForms);
    }

    if (accessRights.kpiBenchmarkingProcurement) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingProcurement);
    }

    if (accessRights.kpiBenchmarkingViewing) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.KpiBenchmarkingViewing);
    }

    if (accessRights.accountingStandardizationInputForms) {
        result.push(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.AccountingStandardizationInputForms
        );
    }

    if (accessRights.accountingStandardizationProcurement) {
        result.push(
            ASSOCIATE_MEMBER_RIGHTS_ENUM.AccountingStandardizationProcurement
        );
    }

    if (accessRights.practiceSalesInputForms) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesInputForms);
    }

    if (accessRights.practiceSalesProcurement) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesProcurement);
    }

    if (accessRights.practiceSalesViewing) {
        result.push(ASSOCIATE_MEMBER_RIGHTS_ENUM.PracticeSalesViewing);
    }

    return result;
};
